import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, CheckCircle, FileText, Calculator, Building2, HeartPulse, BookOpen, Shield, Users, Clock, MapPin, Award, Briefcase, ChevronRight, Calendar, Facebook, Instagram, CreditCard } from "lucide-react";
import ContactForm from "../components/ContactForm";
import TamilNaduMap from "../components/TamilNaduMap";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";
import { blogPosts } from "../data/blogPosts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20consultation%20for%20tax%20services%20in%20Coimbatore.%20Please%20assist%20me";
const PHONE_URL = "tel:+918940988776";

const HomePage = () => {
  const services = [
    {
      icon: <FileText size={32} />,
      title: "GST Registration",
      description: "Quick and hassle-free GST registration for businesses of all sizes in Coimbatore. Expert guidance, transparent pricing, and 100% compliance assurance. Get registered in 7 days.",
      href: "/gst-registration",
      cta: "Learn More",
    },
    {
      icon: <Calculator size={32} />,
      title: "GST Return Filing",
      description: "Timely and accurate monthly, quarterly, and annual GST return filing services. Zero penalties, expert handling, and complete compliance. File with confidence.",
      href: "/gst-return-filing",
      cta: "Learn More",
    },
    {
      icon: <FileText size={32} />,
      title: "E-Commerce GST Registration",
      description: "Mandatory GST registration for sellers on Amazon, Flipkart, Meesho, Myntra and all marketplaces. Section 24(ix) compliance — get your GSTIN in 3–7 working days.",
      href: "/ecom-gst-registration",
      cta: "Learn More",
    },
    {
      icon: <Calculator size={32} />,
      title: "E-Commerce GST Filing",
      description: "Monthly GSTR-1, GSTR-3B filing and TCS reconciliation for e-commerce sellers. Match marketplace data, claim every TCS credit, avoid Rule 88C notices.",
      href: "/ecom-gst-filing",
      cta: "Learn More",
    },
    {
      icon: <Briefcase size={32} />,
      title: "Income Tax Return (ITR) Filing",
      description: "Expert ITR filing services for salaried individuals, professionals, and businesses. Maximize refunds, ensure accuracy, and stay compliant. Fast processing guaranteed.",
      href: "/itr-filing",
      cta: "Learn More",
    },
    {
      icon: <BookOpen size={32} />,
      title: "TDS Filing",
      description: "Complete TDS compliance including quarterly returns, certificates, and reconciliation. Expert handling, timely filing, and complete documentation.",
      href: "/tds-filing",
      cta: "Learn More",
    },
    {
      icon: <Building2 size={32} />,
      title: "Business Registration",
      description: "Proprietorship, MSME, Startup India registration and company incorporation support. Complete documentation, expert guidance, and hassle-free registration.",
      href: "/business-registration",
      cta: "Learn More",
    },
    {
      icon: <HeartPulse size={32} />,
      title: "Health Insurance Advisory",
      description: "Expert guidance on health insurance plans for individuals and group coverage for businesses. Affordable plans, maximum coverage, and personalized recommendations.",
      href: "/health-insurance-advisory",
      cta: "Get Quote",
    },
    {
      icon: <Briefcase size={32} />,
      title: "Business Loan Assistance",
      description: "End-to-end business loan assistance in Coimbatore for MSMEs, startups, and established businesses. Documentation support, bank coordination, and personalized advisory.",
      href: "/business-loan-assistance",
      cta: "Apply Now",
    },
    {
      icon: <CreditCard size={32} />,
      title: "Personal Loan Assistance",
      description: "Expert personal loan assistance in Coimbatore for salaried and self-employed individuals. Eligibility assessment, documentation support, and bank coordination for faster processing.",
      href: "/personal-loan-assistance",
      cta: "Apply Now",
    },
  ];

  const whyChooseUs = [
    { icon: <MapPin size={24} />, text: "Local Tax Consultant in Coimbatore - Expert knowledge of Tamil Nadu tax regulations" },
    { icon: <CheckCircle size={24} />, text: "100% Compliance Support - Zero penalties, complete accuracy guaranteed" },
    { icon: <Award size={24} />, text: "Affordable & Transparent Fees - No hidden charges, clear pricing upfront" },
    { icon: <Users size={24} />, text: "Personalized Advisory - Customized solutions for your business needs" },
    { icon: <Shield size={24} />, text: "Digital & Paperless Process - Secure online document submission and tracking" },
    { icon: <Clock size={24} />, text: "On-Time Filing Assurance - Never miss a deadline with our expert team" },
  ];

  const processSteps = [
    { number: "01", title: "Free Consultation", description: "Free initial consultation to understand your tax and compliance needs. No obligation, no pressure." },
    { number: "02", title: "Document Collection", description: "We collect required documents through our secure digital portal. Simple, safe, and hassle-free." },
    { number: "03", title: "Filing & Compliance", description: "Our experts handle all filings accurately and on time. 100% compliance guaranteed." },
    { number: "04", title: "Ongoing Support", description: "Continuous support for queries and future compliance needs. We're here for you year-round." },
  ];

  const testimonials = [
    {
      name: "Senthil Kumar",
      role: "Textile Manufacturing Owner, Tirupur",
      image: "https://customer-assets.emergentagent.com/job_compliance-pro-24/artifacts/xqo7exvs_a-smiling-young-indian-man-with-formal-shirts-outdoors-photo.jpg",
      review: "WINTRUST SOLUTIONS has been handling our GST compliance for 3 years. Their professionalism, timely filing, and expert guidance have given us complete peace of mind. Highly recommended!",
    },
    {
      name: "Lakshmi Narayanan",
      role: "IT Services Startup Founder, Coimbatore",
      image: "https://customer-assets.emergentagent.com/job_compliance-pro-24/artifacts/kyw8e1kz_images%20%289%29.jpeg",
      review: "As a first-time entrepreneur, I was overwhelmed with tax compliance. The team at WINTRUST made everything simple and stress-free. They explained everything clearly and handled all the paperwork. Best decision ever!",
    },
    {
      name: "Murugan Rajan",
      role: "Retail Business Owner, Coimbatore",
      image: "https://customer-assets.emergentagent.com/job_compliance-pro-24/artifacts/egyim263_confident-indian-ceo-modern-office-portrait_875825-184553.jpg",
      review: "Excellent service and transparent pricing. They helped us save significantly on our taxes through proper planning and compliance.",
    },
  ];

  const faqs = [
    {
      question: "Do you provide GST services in Coimbatore?",
      answer: "Yes, we provide comprehensive GST services including registration, monthly/quarterly return filing, annual returns, amendments, and cancellation services throughout Coimbatore and Tamil Nadu.",
    },
    {
      question: "Can salaried individuals file ITR through you?",
      answer: "Absolutely! We offer ITR filing services for salaried individuals, including those with salary income, house property income, capital gains, and other sources. We help maximize your tax savings through proper deductions.",
    },
    {
      question: "Do you handle complete compliance for MSMEs?",
      answer: "Yes, we provide end-to-end compliance support for MSMEs including GST registration and filing, income tax, TDS, annual filings, and regulatory compliance. We ensure you stay compliant without any hassles.",
    },
    {
      question: "Do you offer health insurance advisory?",
      answer: "Yes, we provide health insurance advisory services to help individuals and businesses choose the right coverage. We help you understand policy terms, compare plans, and select coverage that fits your needs and budget.",
    },
    {
      question: "What are your service charges?",
      answer: "Our pricing is transparent and competitive. We offer customized packages based on your specific requirements. Contact us for a free consultation and detailed quote.",
    },
  ];

  useCanonical("/");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "WINTRUST SOLUTIONS",
    "description": "Expert tax consultant in Coimbatore providing GST registration, return filing, ITR, TDS, business registration and health insurance advisory services.",
    "url": "https://wintrustsolutions.com",
    "telephone": "+918940988776",
    "email": "wintrustsolutions@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Coimbatore",
      "addressRegion": "Tamil Nadu",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "11.0168",
      "longitude": "76.9558"
    },
    "openingHours": "Mo-Sa 09:00-18:00",
    "priceRange": "$$",
    "areaServed": ["Coimbatore", "Tirupur", "Erode", "Salem", "Pollachi"]
  });

  return (
    <>
      <Helmet>
        <title>Tax Consultant in Coimbatore | GST, ITR, TDS Filing | WINTRUST SOLUTIONS</title>
        <meta name="description" content="WINTRUST SOLUTIONS - Expert tax consultant in Coimbatore for GST registration, return filing, ITR, TDS, business registration & health insurance advisory. 100% compliance support." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-navy hero-pattern relative overflow-hidden">
        <div className="container-custom section-padding">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-heading">
                GST, Insurance & Loan Assistance Services in Coimbatore
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl">
                Reliable GST registration, return filing, ITR, TDS, business & personal loan assistance, and health insurance advisory services in Coimbatore. 100% compliance support for MSMEs, startups, and salaried professionals. Transparent pricing, quick response, and local expertise you can trust.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="btn-primary text-center">
                  Book Free Consultation
                </a>
                <a
                  id="phone-hero"
                  href={PHONE_URL}
                  data-location="hero"
                  className="btn-secondary !border-white !text-white hover:!bg-white hover:!text-navy flex items-center justify-center gap-2"
                >
                  <Phone size={18} />
                  Call Now
                </a>
                <a
                  id="whatsapp-hero"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-location="hero"
                  className="btn-whatsapp justify-center"
                >
                  <MessageCircle size={18} />
                  WhatsApp
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gold/10 rounded-lg transform rotate-3"></div>
                <img
                  src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&fm=webp&q=60"
                  alt="Professional Tax Consultation Services in Coimbatore"
                  className="relative rounded-lg shadow-2xl w-full object-cover"
                  width="600"
                  height="400"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="bg-white py-8 border-b border-slate-100">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: <CheckCircle className="text-gold" size={24} />, text: "100% Compliance Support" },
              { icon: <Award className="text-gold" size={24} />, text: "Transparent Pricing" },
              { icon: <Clock className="text-gold" size={24} />, text: "Quick Response" },
              { icon: <MapPin className="text-gold" size={24} />, text: "Local Expertise in Coimbatore" },
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3 justify-center md:justify-start">
                {item.icon}
                <span className="text-navy font-medium text-sm md:text-base">{item.text}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-6">
            100% Digital | Compliance-First | Coimbatore's Trusted Tax Consultants
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-4 font-heading">
              Our Tax, GST, and Health Insurance Services in Coimbatore
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive tax and compliance solutions tailored for businesses and individuals in Coimbatore. From GST registration to ITR filing, we handle all your compliance needs with 100% accuracy and on-time delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <article key={index} className="service-card group">
                <div className="service-icon text-navy mb-4">{service.icon}</div>
                <h3 className="text-xl font-semibold text-navy mb-3 font-heading">{service.title}</h3>
                <p className="text-slate-600 mb-4 leading-relaxed">{service.description}</p>
                <Link
                  to={service.href}
                  className="text-gold font-medium hover:text-gold-dark inline-flex items-center gap-1"
                >
                  {service.cta}
                  <ChevronRight size={16} />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="bg-white section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-8 font-heading">
                Why Choose WINTRUST SOLUTIONS for Your Tax Compliance?
              </h2>
              <ul className="space-y-4">
                {whyChooseUs.map((reason, index) => (
                  <li key={index} className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50">
                    <div className="text-gold flex-shrink-0 mt-1">{reason.icon}</div>
                    <span className="text-navy">{reason.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-navy/5 rounded-2xl transform -rotate-2"></div>
              <div className="relative bg-white rounded-xl shadow-lg p-6" data-testid="why-choose-image">
                <TamilNaduMap />
              </div>
              <p className="mt-3 text-xs text-slate-500 text-center italic">
                Headquartered in Coimbatore — serving businesses across all 38 districts of Tamil Nadu.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-4 font-heading">
              Our Simple 4-Step Tax Compliance Process
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Get started with your tax compliance in four easy steps. From consultation to ongoing support, we handle everything.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center relative">
                <div className="w-16 h-16 bg-navy rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-gold font-bold text-xl">{step.number}</span>
                </div>
                <h3 className="text-xl font-semibold text-navy mb-2 font-heading">{step.title}</h3>
                <p className="text-slate-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="bg-white section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-4 font-heading">
              What Our Clients Say About WINTRUST SOLUTIONS
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Serving MSMEs, Startups &amp; Professionals across Coimbatore, Tirupur &amp; Tamil Nadu.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <article key={index} className="testimonial-card">
                <blockquote className="text-slate-700 mb-6 italic">"{testimonial.review}"</blockquote>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold text-navy">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Google Review Links */}
          <div className="mt-10 flex flex-wrap justify-center items-center gap-x-10 gap-y-4 text-center" data-testid="gbp-review-links">
            <a
              href="https://maps.app.goo.gl/uap1Kkj2XWsWs5FKA"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold font-semibold hover:underline underline-offset-4 transition-all"
              data-testid="view-google-reviews-link"
            >
              ⭐ View Our Google Reviews →
            </a>
            <a
              href="https://g.page/r/CVQurjZHtTQ9EAE/review"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gold font-semibold hover:underline underline-offset-4 transition-all"
              data-testid="leave-review-link"
            >
              ✏️ Leave Us a Review →
            </a>
          </div>
        </div>
      </section>
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-4 font-heading">
              Latest Tax Tips & Compliance Guides
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Stay informed with expert insights on GST, ITR, TDS, and business compliance from our Coimbatore tax consultants.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {blogPosts.slice(0, 3).map((post) => (
              <article key={post.slug} className="bg-white rounded-lg border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden group" data-testid={`recent-blog-${post.slug}`}>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-semibold text-gold bg-gold/10 px-3 py-1 rounded-full">
                      {post.category}
                    </span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Calendar size={12} />
                      {post.date}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-navy mb-3 font-heading leading-snug group-hover:text-gold transition-colors">
                    <Link to={`/blog/${post.slug}`}>
                      {post.title}
                    </Link>
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="text-gold font-medium text-sm hover:text-gold-dark inline-flex items-center gap-1"
                  >
                    Read More <ChevronRight size={14} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="https://wintrustsolutions.blogspot.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-blogs-btn"
            >
              View All Articles <ChevronRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-navy mb-4 font-heading">
              Frequently Asked Questions
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Find answers to common questions about our tax and compliance services in Coimbatore
            </p>
          </div>
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-lg border border-slate-200 px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left text-navy font-medium py-4 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Follow WinTrust Section */}
      <section className="bg-navy section-padding" data-testid="follow-wintrust-section">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-white mb-4 font-heading">
              Follow WINTRUST
            </h2>
            <p className="text-slate-300 mb-8">
              Stay updated with GST alerts, tax deadlines & financial insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.facebook.com/share/1KXgzbQ4zP/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-white/10 border border-white/20 rounded-lg text-white font-medium hover:bg-gold hover:border-gold transition-all"
                data-testid="follow-facebook-btn"
              >
                <Facebook size={20} />
                Follow on Facebook
              </a>
              <a
                href="https://www.instagram.com/wintrustsolutions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-white/10 border border-white/20 rounded-lg text-white font-medium hover:bg-gold hover:border-gold transition-all"
                data-testid="follow-instagram-btn"
              >
                <Instagram size={20} />
                Follow on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <ContactForm />
    </>
  );
};

export default HomePage;
